// frontend/src/components/TechnicalCard/DocumentSearch.jsx

/**
 * Полнотекстовый поиск по листам документа (технической карты).
 *
 * Использует PostgreSQL FTS с русской морфологией на бэкенде:
 * в выдачу попадают конкретные листы с подсветкой найденных совпадений
 * и списком позиций (материалы, операции, оборудование, компоненты).
 */

import { useState, useRef, Fragment } from 'react'
import { Card } from '../Common/Card'
import {
  Search, Loader, FileText, Package, Zap, Wrench,
  AlertCircle, ChevronRight, X
} from 'lucide-react'
import toast from 'react-hot-toast'
import { technicalCardAPI } from '../../services/api'

// Иконки и цвета для типов листов
const SHEET_TYPE_CONFIG = {
  materials: { label: 'Материалы', color: 'bg-blue-100 text-blue-800', icon: Package },
  assembly: { label: 'Сборка', color: 'bg-green-100 text-green-800', icon: Wrench },
  operational: { label: 'Операции', color: 'bg-purple-100 text-purple-800', icon: Zap },
  unknown: { label: 'Неизвестно', color: 'bg-gray-100 text-gray-800', icon: FileText },
}

// Цвета контекстов совпадений
const CONTEXT_COLORS = {
  'Материал': 'bg-blue-50 text-blue-700 border-blue-200',
  'Операция': 'bg-purple-50 text-purple-700 border-purple-200',
  'Оборудование': 'bg-orange-50 text-orange-700 border-orange-200',
  'Компонент': 'bg-green-50 text-green-700 border-green-200',
  'Сборка': 'bg-green-50 text-green-700 border-green-200',
}

const getContextColor = (context) => {
  // "Этап сборки 1", "Этап сборки 2" и т.п.
  if (context?.startsWith('Этап')) return 'bg-teal-50 text-teal-700 border-teal-200'
  return CONTEXT_COLORS[context] || 'bg-gray-50 text-gray-700 border-gray-200'
}

/**
 * Рендер текста с подсветкой совпадений.
 * Бэкенд размечает совпадения маркерами [[слово]].
 */
const HighlightedText = ({ text }) => {
  if (!text) return null

  const parts = text.split(/(\[\[.*?\]\])/g)

  return (
    <>
      {parts.map((part, idx) => {
        if (part.startsWith('[[') && part.endsWith(']]')) {
          return (
            <mark key={idx} className="bg-yellow-200 text-gray-900 font-semibold rounded px-0.5">
              {part.slice(2, -2)}
            </mark>
          )
        }
        return <Fragment key={idx}>{part}</Fragment>
      })}
    </>
  )
}

export const DocumentSearch = ({ cardId, onOpenSheet }) => {
  const [query, setQuery] = useState('')
  const [searching, setSearching] = useState(false)
  const [searchData, setSearchData] = useState(null) // { query, results, total_sheets, searchable_sheets }
  const inputRef = useRef(null)

  const performSearch = async () => {
    const trimmed = query.trim()
    if (!trimmed) {
      toast.error('Введите поисковый запрос')
      inputRef.current?.focus()
      return
    }
    if (trimmed.length < 2) {
      toast.error('Запрос слишком короткий (минимум 2 символа)')
      return
    }

    setSearching(true)
    try {
      const response = await technicalCardAPI.searchInDocument(cardId, trimmed)
      setSearchData(response.data)

      if ((response.data.results || []).length === 0) {
        toast('Ничего не найдено. Попробуйте изменить запрос.', { icon: '🔍' })
      }
    } catch (error) {
      console.error('Document search error:', error)
      toast.error(error.response?.data?.detail || 'Ошибка поиска по документу')
    } finally {
      setSearching(false)
    }
  }

  const handleClear = () => {
    setQuery('')
    setSearchData(null)
    inputRef.current?.focus()
  }

  const results = searchData?.results || []

  return (
    <div className="space-y-4">
      {/* Поисковая строка */}
      <Card>
        <div className="flex items-center gap-2 mb-3">
          <Search size={20} className="text-blue-600" />
          <h3 className="text-lg font-semibold">Поиск по документу</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Полнотекстовый поиск по данным анализа листов: материалы, операции,
          оборудование, компоненты и этапы сборки. Учитывается русская морфология
          (например, «болты» найдёт «болт М10»).
        </p>

        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && performSearch()}
              placeholder="Например: болт М10, сверление, лестница, ГОСТ 7798..."
              maxLength={200}
              disabled={searching}
              className="w-full pl-3 pr-9 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
            />
            {query && (
              <button
                onClick={handleClear}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                title="Очистить"
              >
                <X size={16} />
              </button>
            )}
          </div>
          <button
            onClick={performSearch}
            disabled={searching || !query.trim()}
            className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2 text-sm font-medium"
          >
            {searching ? (
              <Loader size={16} className="animate-spin" />
            ) : (
              <Search size={16} />
            )}
            Найти
          </button>
        </div>

        {/* Сводка результатов */}
        {searchData && !searching && (
          <div className="flex items-center gap-3 mt-3 text-sm text-gray-600">
            {results.length > 0 ? (
              <span>
                По запросу <span className="font-semibold text-gray-900">«{searchData.query}»</span>{' '}
                найдено: <span className="font-semibold text-blue-600">{results.length}</span>{' '}
                {results.length === 1 ? 'лист' : results.length < 5 ? 'листа' : 'листов'}
              </span>
            ) : (
              <span>
                По запросу <span className="font-semibold text-gray-900">«{searchData.query}»</span> ничего не найдено
              </span>
            )}
            {searchData.searchable_sheets < searchData.total_sheets && (
              <span className="text-xs text-yellow-600">
                (доступно для поиска: {searchData.searchable_sheets} из {searchData.total_sheets} листов —
                остальные ещё не проанализированы)
              </span>
            )}
          </div>
        )}
      </Card>

      {/* Индикатор загрузки */}
      {searching && (
        <Card>
          <div className="flex flex-col items-center justify-center py-10 gap-3">
            <Loader size={32} className="animate-spin text-blue-600" />
            <p className="text-sm text-gray-600">Поиск по листам документа...</p>
          </div>
        </Card>
      )}

      {/* Результаты поиска */}
      {!searching && results.length > 0 && (
        <div className="space-y-3">
          {results.map((result) => {
            const typeConfig = SHEET_TYPE_CONFIG[result.sheet_type] || SHEET_TYPE_CONFIG.unknown
            const TypeIcon = typeConfig.icon

            return (
              <Card key={result.sheet_id} className="hover:shadow-md transition-shadow">
                <div className="flex gap-4">
                  {/* Миниатюра листа */}
                  <div className="flex-shrink-0">
                    {result.image_url ? (
                      <img
                        src={result.image_url}
                        alt={`Лист ${result.page_number}`}
                        className="w-24 h-32 object-cover rounded-lg border bg-gray-50"
                        loading="lazy"
                        onError={(e) => { e.target.style.display = 'none' }}
                      />
                    ) : (
                      <div className="w-24 h-32 flex items-center justify-center rounded-lg border bg-gray-50">
                        <FileText size={28} className="text-gray-300" />
                      </div>
                    )}
                  </div>

                  {/* Информация о листе и совпадения */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-gray-900">
                          Лист {result.page_number}
                        </h4>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${typeConfig.color}`}>
                          <TypeIcon size={12} />
                          {typeConfig.label}
                        </span>
                        {/* Релевантность */}
                        <span className="text-xs text-gray-400">
                          релевантность: {(result.rank * 100).toFixed(0)}%
                        </span>
                      </div>
                      {onOpenSheet && (
                        <button
                          onClick={() => onOpenSheet(result.sheet_id, result.page_number)}
                          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 hover:underline font-medium"
                        >
                          Открыть лист
                          <ChevronRight size={14} />
                        </button>
                      )}
                    </div>

                    {/* Подсветка совпадений из ts_headline */}
                    {result.headline && (
                      <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-2 mb-2 leading-relaxed">
                        <HighlightedText text={result.headline} />
                      </p>
                    )}

                    {/* Конкретные найденные позиции */}
                    {result.matches && result.matches.length > 0 && (
                      <div className="space-y-1.5">
                        <p className="text-xs font-medium text-gray-500">
                          Найденные позиции:
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {result.matches.map((match, idx) => (
                            <span
                              key={idx}
                              className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded border ${getContextColor(match.context)}`}
                              title={match.text}
                            >
                              <span className="font-semibold">{match.context}:</span>
                              <span className="truncate max-w-[280px]">{match.text}</span>
                            </span>
                          ))}
                          {result.matches_total > result.matches.length && (
                            <span className="inline-flex items-center px-2 py-1 text-xs rounded border bg-gray-50 text-gray-500 border-gray-200">
                              и ещё {result.matches_total - result.matches.length}...
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            )
          })}
        </div>
      )}

      {/* Пустая выдача */}
      {!searching && searchData && results.length === 0 && (
        <Card>
          <div className="text-center py-10">
            <AlertCircle size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-600 font-medium mb-1">Ничего не найдено</p>
            <p className="text-sm text-gray-500">
              Попробуйте изменить запрос: используйте другие слова или
              проверьте, что листы документа проанализированы.
            </p>
          </div>
        </Card>
      )}

      {/* Начальное состояние */}
      {!searching && !searchData && (
        <Card>
          <div className="text-center py-10">
            <Search size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-600 font-medium mb-1">
              Введите запрос для поиска по документу
            </p>
            <p className="text-sm text-gray-500">
              Поиск выполняется по материалам, операциям, оборудованию,
              компонентам и этапам сборки на всех листах.
            </p>
          </div>
        </Card>
      )}
    </div>
  )
}

export default DocumentSearch
